/**
 * Pure, deterministische score-engine voor beachvolleybal (fase 1).
 *
 * - Geen camera-, DOM- of audio-afhankelijkheden (draait in browser én Node).
 * - Toestand wordt ALTIJD afgeleid door de append-only eventlog te "replayen":
 *   rollback/undo = herleide staat opnieuw opbouwen met een skip-range.
 *   Daardoor herstelt elke correctie alle afgeleide staat: score, serveerder,
 *   serveervolgorde, kantwisseldrempels, setstand en wedstrijdstatus.
 * - Elk punt is herleidbaar tot exact één rally (unieke rallyId).
 * - Idempotent: herhaalde camera-events met dezelfde detectorId veranderen
 *   de stand nooit opnieuw (geen dubbele punten).
 *
 * Beslisvolgorde (zie opdracht):
 *   A. RALLY_RESULT_CANDIDATE -> high: voorlopig punt; medium: voorstel;
 *      laag: ended_unknown (geen punt).
 *   B. Volgende SERVE_DETECTED -> bevestigen (geen tweede punt), corrigeren
 *      (rollback), of fallback-beslissing (precies één punt); onzeker bewijs
 *      => defer + "controle nodig" (geen gok).
 *   C. Eerste opslag van een set levert NOOIT een punt. Een opslag kan alleen
 *      een vorige, geëindigde en nog niet verwerkte rally valideren.
 *   D. Handmatige invoer heeft voorrang en wordt nooit automatisch
 *      overschreven (alleen een waarschuwing bij tegenspraak).
 */

import { assertConfig, defaultConfig } from "./config";
import type {
  DeferredServe,
  DomainEvent,
  Effect,
  MatchState,
  PhysicalSide,
  PlayerIndex,
  RallyRecord,
  SetRecord,
  TeamId,
} from "./types";

const OPPOSITE_SIDE: Record<PhysicalSide, PhysicalSide> = { left: "right", right: "left" };

function r3<T>(a: T, b: T): Record<TeamId, T> {
  return { A: a, B: b };
}

/* ------------------------------------------------------------------ */
/* Basistoestand                                                       */
/* ------------------------------------------------------------------ */

export function createEmptySet(
  setNumber: number,
  firstServeTeam: TeamId,
  firstInOrder: Record<TeamId, PlayerIndex> = r3(0, 0),
): SetRecord {
  return { setNumber, firstServeTeam, firstInOrder: { ...firstInOrder }, points: r3(0, 0), sideSwitchDone: 0, winner: null };
}

export function createInitialState(): MatchState {
  return {
    config: defaultConfig(),
    phase: "setup",
    setNumber: 0,
    setsWon: r3(0, 0),
    currentSet: createEmptySet(0, "A"),
    physicalSides: r3("left", "right"),
    rally: null,
    history: [],
    nextServe: null,
    deferredServe: null,
    matchWinner: null,
    seq: 0,
  };
}

/* ------------------------------------------------------------------ */
/* Afgeleid serveerplan (volledig uit de finale geschiedenis)          */
/* ------------------------------------------------------------------ */

export interface ServePlanEntry {
  rallyId: string;
  setNumber: number;
  team: TeamId;
  playerIndex: PlayerIndex;
}

interface ServeInput {
  id: string;
  setNumber: number;
  winner: TeamId | null;
}

/**
 * Serveerplan voor een set, afgeleid uit resultaten.
 *
 * Regels (zoals gevraagd):
 *  - Eerste opslag van een set: firstServeTeam, speler op orderpositie 0.
 *  - Wint het serverende team, dan blijft dezelfde speler serveren.
 *  - Side-out: het winnende team serveert de volgende rally met de ANDERE
 *    speler dan de laatste keer dat dit team serveerde (of de eerste
 *    serveerder wanneer het team nog niet serveerde in deze set).
 */
export function computeServePlan(inputs: ServeInput[], firstServeTeam: TeamId): ServePlanEntry[] {
  const plan: ServePlanEntry[] = [];
  for (let k = 0; k < inputs.length; k++) {
    const inp = inputs[k];
    const team: TeamId = k === 0 ? firstServeTeam : inputs[k - 1].winner ?? firstServeTeam;
    let playerIndex: PlayerIndex;
    if (k > 0 && plan[k - 1].team === team) {
      playerIndex = plan[k - 1].playerIndex; //zelfde speler serveert opnieuw
    } else {
      let lastIdx: number | null = null;
      for (let j = k - 1; j >= 0; j--) {
        if (plan[j].team === team) {
          lastIdx = j;
          break;
        }
      }
      playerIndex = lastIdx === null ? 0 : ((1 - plan[lastIdx].playerIndex) as PlayerIndex);
    }
    plan.push({ rallyId: inp.id, setNumber: inp.setNumber, team, playerIndex });
  }
  return plan;
}

export function buildServePlan(history: RallyRecord[], firstServeTeam: TeamId): ServePlanEntry[] {
  return computeServePlan(
    history.map((r) => ({ id: r.id, setNumber: r.setNumber, winner: r.result?.winnerTeam ?? null })),
    firstServeTeam,
  );
}

/** Serveerplan inclusief de lopende rally (als die al een (voorlopige) winnaar heeft). */
export function servePlan(state: MatchState): ServePlanEntry[] {
  const hist = state.history.filter((r) => r.setNumber === state.setNumber);
  const inputs: ServeInput[] = hist.map((r) => ({ id: r.id, setNumber: r.setNumber, winner: r.result?.winnerTeam ?? null }));
  if (state.rally && state.rally.setNumber === state.setNumber && state.rally.result?.winnerTeam) {
    inputs.push({ id: state.rally.id, setNumber: state.rally.setNumber, winner: state.rally.result.winnerTeam });
  }
  return computeServePlan(inputs, state.currentSet.firstServeTeam);
}

/** Wie serveert de HUIDIGE (openstaande) rally (of de eerstvolgende). */
export function getCurrentServe(state: MatchState): { team: TeamId; playerIndex: PlayerIndex } | null {
  if (!state.rally || state.setNumber < 1) return null;
  const hist = state.history.filter((r) => r.setNumber === state.setNumber);
  const inputs: ServeInput[] = hist.map((r) => ({ id: r.id, setNumber: r.setNumber, winner: r.result?.winnerTeam ?? null }));
  inputs.push({ id: state.rally.id, setNumber: state.rally.setNumber, winner: null });
  const plan = computeServePlan(inputs, state.currentSet.firstServeTeam);
  const last = plan[plan.length - 1];
  return { team: last.team, playerIndex: last.playerIndex };
}

/** Wie serveert de volgende rally (na verwerking van de lopende, indien die een winnaar heeft). */
export function deriveNextServe(state: MatchState): { team: TeamId; playerIndex: PlayerIndex } | null {
  if (state.phase === "setup" || state.matchWinner !== null || state.setNumber < 1) return null;
  const plan = servePlan(state);
  const pendingWinner = state.rally?.result?.winnerTeam ?? null;
  if (plan.length === 0) return { team: state.currentSet.firstServeTeam, playerIndex: 0 };
  const lastWinner: TeamId | null = pendingWinner ?? state.history.filter((r) => r.setNumber === state.setNumber).at(-1)?.result?.winnerTeam ?? null;
  if (!lastWinner) return null;
  const last = plan[plan.length - 1];
  if (last.team === lastWinner) return { team: lastWinner, playerIndex: last.playerIndex };
  let j = plan.length - 1;
  while (j >= 0 && plan[j].team !== lastWinner) j--;
  return { team: lastWinner, playerIndex: j < 0 ? 0 : ((1 - plan[j].playerIndex) as PlayerIndex) };
}

/* ------------------------------------------------------------------ */
/* Kantwissel en set/match-einde                                       */
/* ------------------------------------------------------------------ */

export function sideSwitchEveryFor(state: MatchState): number {
  return state.setNumber >= 3 ? state.config.sideSwitchEvery.thirdSet : state.config.sideSwitchEvery.normal;
}

export interface SideSwitchStatus {
  pending: number;
  totalPoints: number;
  nextThreshold: number;
}

export function getSideSwitchStatus(state: MatchState): SideSwitchStatus {
  const total = state.currentSet.points.A + state.currentSet.points.B;
  const every = sideSwitchEveryFor(state);
  const due = Math.floor(total / every);
  const pending = Math.max(0, due - state.currentSet.sideSwitchDone);
  return { pending, totalPoints: total, nextThreshold: (state.currentSet.sideSwitchDone + 1) * every };
}

function checkSetEnd(state: MatchState): { setWinner: TeamId | null; matchWinner: TeamId | null } {
  const { points } = state.currentSet;
  const target = state.config.setTargets[state.setNumber - 1] ?? state.config.setTargets[state.config.setTargets.length - 1];
  const lead = state.config.minLead;
  let setWinner: TeamId | null = null;
  if (points.A >= target && points.A - points.B >= lead) setWinner = "A";
  else if (points.B >= target && points.B - points.A >= lead) setWinner = "B";
  if (!setWinner) return { setWinner: null, matchWinner: null };
  const setsWon = { ...state.setsWon, [setWinner]: state.setsWon[setWinner] + 1 };
  const needed = Math.ceil(state.config.bestOf / 2);
  return { setWinner, matchWinner: setsWon[setWinner] >= needed ? setWinner : null };
}

function addPoint(state: MatchState, team: TeamId, delta: 1 | -1): void {
  const next = state.currentSet.points[team] + delta;
  if (next < 0) throw new Error(`punt kan niet negatief worden (${team}: ${next})`);
  state.currentSet.points[team] = next;
}

export function sideToTeam(state: MatchState, side: PhysicalSide): TeamId | null {
  if (state.physicalSides.A === side) return "A";
  if (state.physicalSides.B === side) return "B";
  return null;
}

/* ------------------------------------------------------------------ */
/* Context + rallyconstructie                                          */
/* ------------------------------------------------------------------ */

interface SkipRange {
  from: number;
  to: number;
}

interface RunCtx {
  skips: SkipRange[];
  /** rallyId -> index van het event dat de rally creëerde (voor UNDO). */
  creationIdx: Map<string, number>;
}

interface EventCtx {
  run: RunCtx;
  effects: Effect[];
  pendingBefore: number;
}

function newRallyId(state: MatchState): string {
  state.seq += 1;
  return `R${state.seq}`;
}

function makeRally(state: MatchState): RallyRecord {
  return {
    id: newRallyId(state),
    detectorId: null,
    setNumber: state.setNumber,
    phase: "serve_candidate",
    serve: null,
    started: false,
    endedAt: null,
    result: null,
  };
}

function registerCreation(idx: number, rally: RallyRecord, ctx: EventCtx): void {
  ctx.run.creationIdx.set(rally.id, idx);
}

function findRallyByDetectorId(state: MatchState, detectorId: string): RallyRecord | null {
  if (state.rally?.detectorId === detectorId) return state.rally;
  for (let i = state.history.length - 1; i >= 0; i--) {
    if (state.history[i].detectorId === detectorId) return state.history[i];
  }
  return null;
}

/** Laatste rally met een punt (voorlopig of definitief). */
function findLastScoredRally(state: MatchState): RallyRecord | null {
  if (state.rally?.result?.winnerTeam) return state.rally;
  for (let i = state.history.length - 1; i >= 0; i--) {
    if (state.history[i].result?.winnerTeam) return state.history[i];
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* Eventtoepassing                                                     */
/* ------------------------------------------------------------------ */

function applyEvent(eventIdx: number, state: MatchState, ev: DomainEvent, ctx: EventCtx): void {
  switch (ev.type) {
    case "MATCH_CONFIGURED":
      if (state.phase !== "setup" || state.setNumber !== 0) return;
      assertConfig(ev.config);
      state.config = ev.config;
      return;
    case "SET_STARTED":
      applySetStarted(state, ev, ctx);
      return;
    case "SERVE_DETECTED":
      applyServeDetected(eventIdx, state, ev, ctx);
      return;
    case "RALLY_STARTED":
      applyRallyStarted(state, ev, ctx);
      return;
    case "RALLY_ENDED":
      applyRallyEnded(state, ev, ctx);
      return;
    case "RALLY_RESULT_CANDIDATE":
      applyCandidate(state, ev, ctx);
      return;
    case "RALLY_RESULT_CONFIRMED":
      applyConfirm(state, ev, ctx);
      return;
    case "MANUAL_POINT_ASSIGNED":
      applyManual(eventIdx, state, ev, ctx);
      return;
    case "POINT_CORRECTED":
      applyCorrected(eventIdx, state, ev, ctx);
      return;
    case "SIDE_SWITCH_CONFIRMED":
      applySideSwitch(state, ev, ctx);
      return;
    case "SIDES_SWAP_MANUAL":
      // zijde-correctie zonder wedstrijdstaat te verliezen (kalibratiefout)
      state.physicalSides = { A: OPPOSITE_SIDE[state.physicalSides.A], B: OPPOSITE_SIDE[state.physicalSides.B] };
      ctx.effects.push({ kind: "warning", message: "fysieke zijden handmatig omgewisseld" });
      return;
    case "UNDO_LAST_RALLY":
      // wordt afgehandeld in de reducer (recursie + skip-range)
      ctx.effects.push({ kind: "nothing_to_undo" });
      return;
  }
}

function applySetStarted(state: MatchState, ev: Extract<DomainEvent, { type: "SET_STARTED" }>, ctx: EventCtx): void {
  if (state.matchWinner) return invalid(ctx, "SET_STARTED na match_win");
  if (state.setNumber === 0) {
    if (ev.setNumber !== 1) return invalid(ctx, `eerste set moet set 1 zijn (kreeg ${ev.setNumber})`);
  } else {
    if (ev.setNumber !== state.setNumber + 1) return invalid(ctx, `setten volgen sequentieel (kreeg ${ev.setNumber}, verwacht ${state.setNumber + 1})`);
    if (!state.currentSet.winner) return invalid(ctx, "vorige set heeft nog geen winnaar");
    const needed = Math.ceil(state.config.bestOf / 2);
    if (state.setsWon[state.currentSet.winner] >= needed) return invalid(ctx, "wedstrijd is al beslist");
  }
  if (state.rally) return invalid(ctx, "er loopt nog een open rally; sluit die eerst af");
  state.setNumber = ev.setNumber;
  state.currentSet = createEmptySet(ev.setNumber, ev.firstServeTeam, ev.initialServers);
  state.physicalSides = { ...ev.startSides };
  state.rally = null;
  state.deferredServe = null;
  state.phase = "awaiting_first_serve";
  ctx.effects.push({ kind: "set_started", setNumber: ev.setNumber });
}

/* ------------------------- SERVE_DETECTED -------------------------- */

type ServeDecision =
  | { kind: "confirm"; team: TeamId }
  | { kind: "confirm_score"; team: TeamId }
  | { kind: "correct"; from: TeamId; to: TeamId }
  | { kind: "fallback"; team: TeamId }
  | { kind: "defer"; reason: string };

function resolveWithServe(state: MatchState, r: RallyRecord, ev: Extract<DomainEvent, { type: "SERVE_DETECTED" }>): ServeDecision {
  if (ev.side === null) {
    return { kind: "defer", reason: "opslagkant onbekend; kan vorige rally niet valideren" };
  }
  const mapped = sideToTeam(state, ev.side);
  if (mapped === null) {
    return { kind: "defer", reason: "opslagkant past niet in de zijdenmapping" };
  }
  const status = r.result?.status ?? "open";
  const winner = r.result?.winnerTeam ?? null;
  switch (status) {
    case "provisional": {
      if (!winner) return { kind: "defer", reason: "provisional zonder winnaar" };
      if (mapped === winner) {
        if (ev.confidence >= state.config.confirmServeConfidence) return { kind: "confirm", team: winner };
        return { kind: "defer", reason: "opslagstemt overeen, maar confidence te laag om te bevestigen" };
      }
      if (ev.confidence >= state.config.authoritativeServeConfidence) return { kind: "correct", from: winner, to: mapped };
      return { kind: "defer", reason: "opslagtegenspraak is niet betrouwbaar genoeg; één tik van gebruiker nodig" };
    }
    case "proposal": {
      if (winner === mapped) return { kind: "confirm_score", team: winner };
      if (ev.confidence >= state.config.authoritativeServeConfidence) return { kind: "fallback", team: mapped };
      return { kind: "defer", reason: "voorstel niet bevestigd; opslagtegenspraak onzeker" };
    }
    case "unknown":
    case "open": {
      // Alleen beslissen als de opslag aantoonbaar betrouwbaar is.
      if (ev.confidence >= state.config.authoritativeServeConfidence) return { kind: "fallback", team: mapped };
      return { kind: "defer", reason: "rally-einde onzeker én opslagdetectie niet betrouwbaar" };
    }
    default:
      return { kind: "defer", reason: `status ${status} niet te valideren` };
  }
}

function applyServeDetected(eventIdx: number, state: MatchState, ev: Extract<DomainEvent, { type: "SERVE_DETECTED" }>, ctx: EventCtx): void {
  // 1) Duplicaat van een al verwerkte opslag? -> nooit een extra punt.
  if (state.rally?.detectorId && ev.detectorId && state.rally.detectorId === ev.detectorId) {
    if (state.rally.phase === "serve_candidate" && state.rally.serve) {
      state.rally.serve = { ...state.rally.serve, side: ev.side, confidence: ev.confidence, ts: ev.ts };
      ctx.effects.push({ kind: "duplicate_ignored", rallyId: state.rally.id, reason: "zelfde opslag nogmaals voor huidige rally" });
      return;
    }
    ctx.effects.push({ kind: "duplicate_ignored", rallyId: state.rally.id, reason: "opslag van deze rally al bekend" });
    return;
  }
  if (ev.detectorId) {
    const known = findRallyByDetectorId(state, ev.detectorId);
    if (known && known.id !== state.rally?.id) {
      ctx.effects.push({ kind: "duplicate_ignored", rallyId: known.id, reason: "opslag al eerder verwerkt" });
      return;
    }
  }

  const pending = state.rally;

  // 2) Geen open rally: begin van een nieuwe rally. NOOIT een punt.
  if (!pending) {
    if (state.phase === "setup" || state.phase === "set_over" || state.phase === "match_over") {
      return invalid(ctx, `opslag kan niet tijdens fase ${state.phase}`);
    }
    const next = deriveNextServe(state);
    if (!next) return invalid(ctx, "geen serveerplan beschikbaar");
    const rally = makeRally(state);
    rally.detectorId = ev.detectorId ?? null;
    rally.serve = { side: ev.side, confidence: ev.confidence, ts: ev.ts };
    state.rally = rally;
    registerCreation(eventIdx, rally, ctx);
    if (state.phase === "awaiting_first_serve") state.phase = "in_play";
    ctx.effects.push({ kind: "rally_created", rallyId: rally.id, team: next.team, playerIndex: next.playerIndex, side: ev.side });
    if (ev.side) {
      const observed = sideToTeam(state, ev.side);
      if (observed && observed !== next.team) {
        ctx.effects.push({ kind: "warning", message: `opslagkant wijst op ${observed}, serveerplan zegt ${next.team}` });
      }
    }
    return;
  }

  // 3) Open rally + opslag: valideer/beslis ALLEEN als de rally geëindigd is.
  if (pending.endedAt === null && pending.result === null) {
    return invalid(ctx, "opslag gedetecteerd terwijl de vorige rally nog loopt (RALLY_ENDED ontbreekt)");
  }

  const decision = resolveWithServe(state, pending, ev);

  switch (decision.kind) {
    case "confirm": {
      const res = pending.result!;
      res.status = "confirmed";
      res.confirmedBy = "serve";
      res.evidence = [...res.evidence, `confirmed_by_serve_${decision.team}`];
      ctx.effects.push({ kind: "point_confirmed", rallyId: pending.id, team: decision.team, confirmedBy: "serve" });
      archiveRally(state);
      createRallyFromServe(state, ev, ctx, eventIdx);
      return;
    }
    case "confirm_score": {
      const res = pending.result!;
      addPoint(state, decision.team, 1);
      res.winnerTeam = decision.team;
      res.status = "confirmed";
      res.source = "vision";
      res.confirmedBy = "serve";
      res.evidence = [...res.evidence, `confirmed_by_serve_${decision.team}`];
      ctx.effects.push({ kind: "point_scored", rallyId: pending.id, team: decision.team, source: "vision", provisional: false });
      afterScoreChanged(state, ctx);
      archiveRally(state);
      createRallyFromServe(state, ev, ctx, eventIdx);
      return;
    }
    case "correct": {
      addPoint(state, decision.from, -1);
      addPoint(state, decision.to, 1);
      const res = pending.result!;
      res.winnerTeam = decision.to;
      res.source = "correction";
      res.confirmedBy = "correction";
      res.status = "confirmed";
      res.evidence = [...res.evidence, `corrected_by_reliable_serve_${decision.to}`];
      ctx.effects.push({ kind: "point_corrected", rallyId: pending.id, from: decision.from, to: decision.to });
      afterScoreChanged(state, ctx);
      archiveRally(state);
      createRallyFromServe(state, ev, ctx, eventIdx);
      return;
    }
    case "fallback": {
      addPoint(state, decision.team, 1);
      const res: NonNullable<RallyRecord["result"]> = pending.result ?? {
        winnerTeam: null, winnerPhysicalSide: null, reason: null, confidence: 0,
        evidence: [], source: null, status: "unknown",
        manual: false, confirmedBy: null, ts: ev.ts,
      };
      pending.result = res;
      res.winnerTeam = decision.team;
      res.winnerPhysicalSide = ev.side;
      res.reason = res.reason ?? "unknown";
      res.source = "serve";
      res.confirmedBy = "serve";
      res.status = "confirmed";
      res.confidence = Math.max(res.confidence, ev.confidence);
      res.evidence = [...res.evidence, `decided_by_serve_${decision.team}`];
      ctx.effects.push({ kind: "point_scored", rallyId: pending.id, team: decision.team, source: "serve", provisional: false });
      afterScoreChanged(state, ctx);
      archiveRally(state);
      createRallyFromServe(state, ev, ctx, eventIdx);
      return;
    }
    case "defer": {
      state.deferredServe = {
        detectorId: ev.detectorId ?? `deferred-${pending.id}`,
        side: ev.side,
        confidence: ev.confidence,
        ts: ev.ts,
      };
      ctx.effects.push({ kind: "serve_deferred", rallyId: pending.id, reason: decision.reason });
      ctx.effects.push({ kind: "ask_user", rallyId: pending.id, reason: decision.reason });
      return;
    }
  }
}

function createRallyFromServe(state: MatchState, ev: Extract<DomainEvent, { type: "SERVE_DETECTED" }>, ctx: EventCtx, eventIdx: number): void {
  const next = deriveNextServe(state);
  if (!next) return;
  const rally = makeRally(state);
  rally.detectorId = ev.detectorId ?? null;
  rally.serve = { side: ev.side, confidence: ev.confidence, ts: ev.ts };
  state.rally = rally;
  registerCreation(eventIdx, rally, ctx);
  ctx.effects.push({ kind: "rally_created", rallyId: rally.id, team: next.team, playerIndex: next.playerIndex, side: ev.side });
}

function createRallyFromDeferred(state: MatchState, d: DeferredServe, ctx: EventCtx, eventIdx: number): void {
  const next = deriveNextServe(state);
  if (!next) return;
  const rally = makeRally(state);
  rally.detectorId = d.detectorId;
  rally.serve = { side: d.side, confidence: d.confidence, ts: d.ts };
  state.rally = rally;
  registerCreation(eventIdx, rally, ctx);
  ctx.effects.push({ kind: "rally_created", rallyId: rally.id, team: next.team, playerIndex: next.playerIndex, side: d.side });
}

function archiveRally(state: MatchState): RallyRecord {
  const r = state.rally!;
  r.phase = "result_confirmed";
  state.history.push(r);
  state.rally = null;
  return r;
}

function afterScoreChanged(state: MatchState, ctx: EventCtx): void {
  const pending = getSideSwitchStatus(state).pending;
  if (pending > ctx.pendingBefore) {
    ctx.effects.push({ kind: "side_switch_pending", setNumber: state.setNumber, totalPoints: state.currentSet.points.A + state.currentSet.points.B });
  }
  const check = checkSetEnd(state);
  if (check.setWinner) {
    ctx.effects.push({ kind: "set_won", setNumber: state.setNumber, team: check.setWinner, points: { ...state.currentSet.points } });
    state.currentSet.winner = check.setWinner;
    state.setsWon = { ...state.setsWon, [check.setWinner]: state.setsWon[check.setWinner] + 1 };
    if (check.matchWinner) {
      state.matchWinner = check.matchWinner;
      state.phase = "match_over";
      ctx.effects.push({ kind: "match_won", team: check.matchWinner });
    } else {
      state.phase = "set_over";
    }
  }
}

/* ------------------------- overige events -------------------------- */

function applyRallyStarted(state: MatchState, ev: Extract<DomainEvent, { type: "RALLY_STARTED" }>, ctx: EventCtx): void {
  const r = state.rally;
  if (!r || r.detectorId !== ev.detectorId) {
    const known = findRallyByDetectorId(state, ev.detectorId);
    if (known) ctx.effects.push({ kind: "duplicate_ignored", rallyId: known.id, reason: "RALLY_STARTED voor afgeronde rally" });
    else invalid(ctx, "RALLY_STARTED voor onbekende rally");
    return;
  }
  if (r.started) {
    ctx.effects.push({ kind: "duplicate_ignored", rallyId: r.id, reason: "RALLY_STARTED al eens toegepast" });
    return;
  }
  r.started = true;
  r.phase = "rally_active";
}

function applyRallyEnded(state: MatchState, ev: Extract<DomainEvent, { type: "RALLY_ENDED" }>, ctx: EventCtx): void {
  const r = state.rally;
  if (!r || r.detectorId !== ev.detectorId) {
    const known = findRallyByDetectorId(state, ev.detectorId);
    if (known) ctx.effects.push({ kind: "duplicate_ignored", rallyId: known.id, reason: "RALLY_ENDED voor afgeronde rally" });
    else invalid(ctx, "RALLY_ENDED voor onbekende rally");
    return;
  }
  if (r.endedAt !== null) {
    ctx.effects.push({ kind: "duplicate_ignored", rallyId: r.id, reason: "RALLY_ENDED al eens toegepast" });
    return;
  }
  r.endedAt = ev.ts;
  r.phase = "rally_end_candidate";
}

function applyCandidate(state: MatchState, ev: Extract<DomainEvent, { type: "RALLY_RESULT_CANDIDATE" }>, ctx: EventCtx): void {
  const r = state.rally;
  if (!r || r.detectorId !== ev.detectorId) {
    const known = findRallyByDetectorId(state, ev.detectorId);
    if (known) ctx.effects.push({ kind: "duplicate_ignored", rallyId: known.id, reason: "RALLY_RESULT_CANDIDATE voor afgeronde rally" });
    else invalid(ctx, "RALLY_RESULT_CANDIDATE voor onbekende rally");
    return;
  }
  if (r.result?.status === "confirmed") {
    ctx.effects.push({ kind: "duplicate_ignored", rallyId: r.id, reason: "resultaat al bevestigd" });
    return;
  }
  if (r.endedAt === null) {
    r.endedAt = ev.ts;
    r.phase = "rally_end_candidate";
  }

  // Upgraden van een bestaand voorlopig resultaat bij strikt hogere confidence.
  if (r.result?.status === "provisional") {
    if (ev.winnerTeamId && r.result.winnerTeam && ev.winnerTeamId !== r.result.winnerTeam) {
      if (ev.confidence > r.result.confidence && ev.confidence >= state.config.highConfidence) {
        const from = r.result.winnerTeam;
        const to = ev.winnerTeamId;
        addPoint(state, from, -1);
        addPoint(state, to, 1);
        r.result = {
          winnerTeam: to,
          winnerPhysicalSide: ev.winnerPhysicalSide,
          reason: ev.reason,
          confidence: ev.confidence,
          evidence: [...ev.evidence, `upgraded_from_${from}`],
          source: "vision",
          status: "provisional",
          manual: false,
          confirmedBy: null,
          ts: ev.ts,
        };
        ctx.effects.push({ kind: "point_corrected", rallyId: r.id, from, to });
        afterScoreChanged(state, ctx);
        return;
      }
      ctx.effects.push({ kind: "duplicate_ignored", rallyId: r.id, reason: "conflicterend candidate zonder hogere confidence" });
      return;
    }
    ctx.effects.push({ kind: "duplicate_ignored", rallyId: r.id, reason: "candidate al verwerkt als provisional" });
    return;
  }

  const conf = ev.confidence;
  if (conf >= state.config.highConfidence && ev.winnerTeamId) {
    r.result = makeResult(ev);
    r.result.status = "provisional";
    r.phase = "result_provisional";
    addPoint(state, ev.winnerTeamId, 1);
    ctx.effects.push({ kind: "point_scored", rallyId: r.id, team: ev.winnerTeamId, source: "vision", provisional: true });
    afterScoreChanged(state, ctx);
    return;
  }
  if (conf >= state.config.mediumConfidence && ev.winnerTeamId) {
    r.result = makeResult(ev);
    r.result.status = "proposal";
    r.phase = "result_uncertain";
    ctx.effects.push({ kind: "proposal_pending", rallyId: r.id, team: ev.winnerTeamId, confidence: conf });
    return;
  }
  r.result = makeResult(ev);
  r.result.status = "unknown";
  r.phase = "result_uncertain";
  ctx.effects.push({ kind: "rally_unknown", rallyId: r.id });
}

function makeResult(ev: Extract<DomainEvent, { type: "RALLY_RESULT_CANDIDATE" }>): NonNullable<RallyRecord["result"]> {
  return {
    winnerTeam: ev.winnerTeamId,
    winnerPhysicalSide: ev.winnerPhysicalSide,
    reason: ev.reason,
    confidence: ev.confidence,
    evidence: [...ev.evidence],
    source: "vision",
    status: "open",
    manual: false,
    confirmedBy: null,
    ts: ev.ts,
  };
}

function applyConfirm(state: MatchState, ev: Extract<DomainEvent, { type: "RALLY_RESULT_CONFIRMED" }>, ctx: EventCtx): void {
  const r = state.rally;
  if (!r) return invalid(ctx, "bevestiging zonder open rally");
  if (ev.detectorId && r.detectorId !== ev.detectorId) return invalid(ctx, "bevestiging voor verkeerde rally");
  const res = r.result;
  if (res?.status === "provisional" && res.winnerTeam) {
    res.status = "confirmed";
    res.confirmedBy = ev.confirmedBy;
    res.evidence = [...res.evidence, `confirmed_by_${ev.confirmedBy}`];
    r.phase = "result_confirmed";
    ctx.effects.push({ kind: "point_confirmed", rallyId: r.id, team: res.winnerTeam, confirmedBy: ev.confirmedBy });
    archiveRally(state);
    return;
  }
  if (res?.status === "proposal" && res.winnerTeam) {
    addPoint(state, res.winnerTeam, 1);
    res.status = "confirmed";
    res.confirmedBy = ev.confirmedBy;
    res.source = "vision";
    res.evidence = [...res.evidence, `confirmed_by_${ev.confirmedBy}`];
    r.phase = "result_confirmed";
    ctx.effects.push({ kind: "point_scored", rallyId: r.id, team: res.winnerTeam, source: "vision", provisional: false });
    afterScoreChanged(state, ctx);
    archiveRally(state);
    return;
  }
  invalid(ctx, `kan ${res?.status ?? "geen resultaat"} niet bevestigen`);
}

function applyManual(eventIdx: number, state: MatchState, ev: Extract<DomainEvent, { type: "MANUAL_POINT_ASSIGNED" }>, ctx: EventCtx): void {
  if (state.matchWinner) return invalid(ctx, "wedstrijd is voorbij");
  let r = state.rally;

  if (!r) {
    if (state.phase === "setup" || state.phase === "set_over" || state.phase === "match_over") {
      return invalid(ctx, `geen punt mogelijk tijdens fase ${state.phase}`);
    }
    if (state.phase === "awaiting_first_serve") state.phase = "in_play";
    const next = deriveNextServe(state);
    r = makeRally(state);
    state.rally = r; // ad-hoc rally is nu de huidige rally
    r.phase = "result_confirmed";
    r.detectorId = ev.detectorId ?? null;
    if (next) r.serve = { side: null, confidence: 0, ts: ev.ts };
    registerCreation(eventIdx, r, ctx);
    ctx.effects.push({ kind: "rally_created", rallyId: r.id, team: next?.team ?? state.currentSet.firstServeTeam, playerIndex: next?.playerIndex ?? 0, side: null });
  }

  const res = r.result;
  if (res?.status === "provisional" && res.winnerTeam && res.winnerTeam !== ev.team) {
    // Handmatige invoer heeft voorrang op camerainferentie (rollback via replay).
    const from = res.winnerTeam;
    addPoint(state, from, -1);
    addPoint(state, ev.team, 1);
    res.winnerTeam = ev.team;
    res.source = "manual";
    res.manual = true;
    res.confirmedBy = "manual";
    res.status = "confirmed";
    res.evidence = [...res.evidence, `manual_override_from_${from}`];
    ctx.effects.push({ kind: "point_corrected", rallyId: r.id, from, to: ev.team });
    afterScoreChanged(state, ctx);
    archiveRally(state);
    handleDeferredAfterManual(state, ev, ctx, eventIdx);
    return;
  }
  if (res?.status === "provisional" && res.winnerTeam === ev.team) {
    res.status = "confirmed";
    res.confirmedBy = "manual";
    res.manual = true;
    res.evidence = [...res.evidence, "manual_confirm"];
    ctx.effects.push({ kind: "point_confirmed", rallyId: r.id, team: ev.team, confirmedBy: "manual" });
    archiveRally(state);
    handleDeferredAfterManual(state, ev, ctx, eventIdx);
    return;
  }
  if (res?.status === "confirmed") {
    ctx.effects.push({ kind: "duplicate_ignored", rallyId: r.id, reason: "punt al verwerkt (confirmed)" });
    return;
  }
  // Geen (voorlopig) resultaat: normaal enkel punt.
  addPoint(state, ev.team, 1);
  if (!res) {
    r.result = {
      winnerTeam: ev.team,
      winnerPhysicalSide: null,
      reason: null,
      confidence: 0,
      evidence: ["manual_point"],
      source: "manual",
      status: "confirmed",
      manual: true,
      confirmedBy: "manual",
      ts: ev.ts,
    };
  } else {
    res.winnerTeam = ev.team;
    res.status = "confirmed";
    res.source = "manual";
    res.manual = true;
    res.confirmedBy = "manual";
    res.evidence = [...res.evidence, "manual_point"];
  }
  ctx.effects.push({ kind: "point_scored", rallyId: r.id, team: ev.team, source: "manual", provisional: false });
  afterScoreChanged(state, ctx);
  archiveRally(state);
  handleDeferredAfterManual(state, ev, ctx, eventIdx);
}

function handleDeferredAfterManual(state: MatchState, ev: Extract<DomainEvent, { type: "MANUAL_POINT_ASSIGNED" }>, ctx: EventCtx, eventIdx: number): void {
  handleDeferredAfterDecision(state, ev.team, ctx, eventIdx);
}

function handleDeferredAfterDecision(state: MatchState, decidedTeam: TeamId, ctx: EventCtx, eventIdx: number): void {
  if (!state.deferredServe) return;
  const d = state.deferredServe;
  state.deferredServe = null;
  const observed = d.side ? sideToTeam(state, d.side) : null;
  if (observed && observed !== decidedTeam) {
    // Een bewuste keuze wordt NOOIT automatisch overschreven; de opslagcontrole
    // toont alleen een waarschuwing.
    ctx.effects.push({ kind: "warning", message: `opslag na handmatige keuze wijst op ${observed}; keuze ${decidedTeam} blijft staan` });
  }
  createRallyFromDeferred(state, d, ctx, eventIdx);
}

function applyCorrected(eventIdx: number, state: MatchState, ev: Extract<DomainEvent, { type: "POINT_CORRECTED" }>, ctx: EventCtx): void {
  const r = findRallyByDetectorId(state, ev.detectorId);
  if (!r) return invalid(ctx, "POINT_CORRECTED voor onbekende rally");
  const res = r.result;
  if (!res) return invalid(ctx, "POINT_CORRECTED zonder resultaat");
  if (res.status === "confirmed" && res.winnerTeam === ev.to) {
    ctx.effects.push({ kind: "duplicate_ignored", rallyId: r.id, reason: "correctie al toegepast" });
    return;
  }
  if (res.winnerTeam !== ev.from) return invalid(ctx, `correctie: huidige winnaar is ${res.winnerTeam}, verwacht ${ev.from}`);
  addPoint(state, ev.from, -1);
  addPoint(state, ev.to, 1);
  res.winnerTeam = ev.to;
  res.source = "correction";
  res.confirmedBy = "correction";
  res.status = "confirmed";
  res.evidence = [...res.evidence, `corrected_to_${ev.to}`];
  ctx.effects.push({ kind: "point_corrected", rallyId: r.id, from: ev.from, to: ev.to });
  afterScoreChanged(state, ctx);
  // Een correctie op de OPENSTAANDE rally sluit deze definitief af.
  if (state.rally?.id === r.id) {
    archiveRally(state);
    handleDeferredAfterDecision(state, ev.to, ctx, eventIdx);
  }
}

function applySideSwitch(state: MatchState, _ev: Extract<DomainEvent, { type: "SIDE_SWITCH_CONFIRMED" }>, ctx: EventCtx): void {
  if (getSideSwitchStatus(state).pending <= 0) {
    ctx.effects.push({ kind: "duplicate_ignored", rallyId: null, reason: "geen kantwissel aan de beurt" });
    return;
  }
  state.physicalSides = { A: OPPOSITE_SIDE[state.physicalSides.A], B: OPPOSITE_SIDE[state.physicalSides.B] };
  state.currentSet.sideSwitchDone += 1;
  ctx.effects.push({ kind: "side_switch_confirmed", setNumber: state.setNumber, flipped: true });
}

function invalid(ctx: EventCtx, reason: string): void {
  ctx.effects.push({ kind: "invalid_event", reason });
}

/* ------------------------------------------------------------------ */
/* Reducer: replay van de volledige eventlog                           */
/* ------------------------------------------------------------------ */

export function reduceEvents(events: readonly DomainEvent[]): { state: MatchState; effects: Effect[][] } {
  const run: RunCtx = { skips: [], creationIdx: new Map() };
  return computeFrom(events, run, 0, events.length, createInitialState(), events.map(() => [] as Effect[]));
}

function computeFrom(
  events: readonly DomainEvent[],
  run: RunCtx,
  from: number,
  to: number,
  state: MatchState,
  effects: Effect[][],
): { state: MatchState; effects: Effect[][] } {
  for (let i = from; i < to; i++) {
    if (run.skips.some((s) => i >= s.from && i < s.to)) continue;
    const ev = events[i];

    if (ev.type === "UNDO_LAST_RALLY") {
      const last = findLastScoredRally(state);
      if (!last) {
        effects[i].push({ kind: "nothing_to_undo" });
        continue;
      }
      const creationIdx = run.creationIdx.get(last.id);
      if (creationIdx === undefined) {
        effects[i].push({ kind: "nothing_to_undo" });
        continue;
      }
      // Rollback: vanaf de creatie van de laatste gescoorde rally wordt de
      // hele reeks overgeslagen en alles opnieuw afgeleid (replay). De
      // herstart stopt vóór het UNDO-event zelf; daarna wordt verdergegaan
      // met de events na het undo-marker.
      const nestedRun: RunCtx = { skips: [...run.skips, { from: creationIdx, to: i }], creationIdx: new Map() };
      const prefix = computeFrom(events, nestedRun, 0, i, createInitialState(), events.map(() => [] as Effect[]));
      effects = prefix.effects;
      return computeFrom(events, nestedRun, i + 1, events.length, prefix.state, effects);
    }

    const ctx: EventCtx = {
      run,
      effects: effects[i],
      pendingBefore: getSideSwitchStatus(state).pending,
    };
    applyEvent(i, state, ev, ctx);
    state.nextServe = deriveNextServe(state);
  }
  return { state, effects };
}

/* ------------------------------------------------------------------ */
/* MatchEngine wrapper                                                 */
/* ------------------------------------------------------------------ */

export class MatchEngine {
  private log: DomainEvent[] = [];
  private cache: { state: MatchState; effects: Effect[] } | null = null;

  constructor(config?: import("./types").MatchConfig) {
    if (config) {
      this.log = [{ type: "MATCH_CONFIGURED", config, ts: 0 }];
    }
  }

  static fromEvents(events: readonly DomainEvent[]): MatchEngine {
    const e = new MatchEngine();
    e.log = [...events];
    return e;
  }

  get events(): readonly DomainEvent[] {
    return this.log;
  }

  get state(): MatchState {
    if (!this.cache) {
      const r = reduceEvents(this.log);
      this.cache = { state: r.state, effects: r.effects[r.effects.length - 1] ?? [] };
    }
    return this.cache.state;
  }

  get lastEffects(): readonly Effect[] {
    if (!this.cache) void this.state;
    return this.cache?.effects ?? [];
  }

  handle(ev: DomainEvent): { state: MatchState; effects: Effect[] } {
    const newLog = [...this.log, ev];
    const r = reduceEvents(newLog);
    this.log = newLog;
    this.cache = { state: r.state, effects: r.effects[newLog.length - 1] ?? [] };
    return { state: this.cache.state, effects: this.cache.effects };
  }

  toJSON(): DomainEvent[] {
    return this.log;
  }
}
